import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { 
  TagIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const CategoryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    category_name: '',
    display_label: '',
    description: '',
    parent_category_id: '',
    sort_order: 1,
    is_active: true
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    fetchCategories();
    if (isEditing) {
      fetchCategory();
    }
  }, [id]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/price-book/categories');
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchCategory = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/price-book/categories/${id}`);
      const category = response.data;
      
      setFormData({
        category_name: category.category_name || '',
        display_label: category.display_label || '',
        description: category.description || '',
        parent_category_id: category.parent_category_id || '',
        sort_order: category.sort_order || 1,
        is_active: category.is_active !== false
      });
    } catch (err) {
      console.error('Error fetching category:', err);
      setError('Failed to load category data');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.category_name.trim()) {
      errors.category_name = 'Category name is required';
    }

    if (!formData.display_label.trim()) {
      errors.display_label = 'Display label is required';
    }

    if (formData.sort_order < 1) {
      errors.sort_order = 'Sort order must be at least 1';
    }

    // Check for circular reference
    if (formData.parent_category_id && isEditing) {
      const wouldCreateCircle = checkCircularReference(formData.parent_category_id, id);
      if (wouldCreateCircle) {
        errors.parent_category_id = 'Cannot select a child category as parent (would create circular reference)';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const checkCircularReference = (parentId, currentId) => {
    // Simple check - in a real app, you'd want to do a more thorough check
    // This prevents direct circular references
    if (parentId === currentId) return true;
    
    // Check if the selected parent is a child of the current category
    const findChildren = (categoryId) => {
      return categories.filter(cat => cat.parent_category_id === parseInt(categoryId));
    };

    const isChild = (potentialChildId, ancestorId) => {
      const children = findChildren(ancestorId);
      for (const child of children) {
        if (child.id === parseInt(potentialChildId)) return true;
        if (isChild(potentialChildId, child.id)) return true;
      }
      return false;
    };

    return isChild(parentId, currentId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = {
        ...formData,
        parent_category_id: formData.parent_category_id || null,
        sort_order: parseInt(formData.sort_order)
      };

      if (isEditing) {
        await api.put(`/price-book/categories/${id}`, submitData);
      } else {
        await api.post('/price-book/categories', submitData);
      }

      navigate('/price-book/categories');
    } catch (err) {
      console.error('Error saving category:', err);
      setError(err.response?.data?.error || 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Filter out current category and its children from parent options
  const getAvailableParentCategories = () => {
    if (!isEditing) return categories;
    
    const currentCategoryId = parseInt(id);
    
    // Get all descendant IDs
    const getDescendantIds = (categoryId) => {
      const descendants = [];
      const children = categories.filter(cat => cat.parent_category_id === categoryId);
      
      for (const child of children) {
        descendants.push(child.id);
        descendants.push(...getDescendantIds(child.id));
      }
      
      return descendants;
    };

    const excludeIds = [currentCategoryId, ...getDescendantIds(currentCategoryId)];
    
    return categories.filter(cat => !excludeIds.includes(cat.id));
  };

  if (loading && isEditing) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/price-book/categories')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEditing ? 'Edit Category' : 'Add New Category'}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {isEditing ? 'Update category information' : 'Create a new price book category'}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Category Name */}
            <div>
              <label htmlFor="category_name" className="block text-sm font-medium text-gray-700">
                Category Name *
              </label>
              <input
                type="text"
                name="category_name"
                id="category_name"
                value={formData.category_name}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.category_name ? 'border-red-300' : ''
                }`}
                placeholder="e.g., consulting, software-development"
              />
              {validationErrors.category_name && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.category_name}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Internal name used for system identification (lowercase, hyphens allowed)
              </p>
            </div>

            {/* Display Label */}
            <div>
              <label htmlFor="display_label" className="block text-sm font-medium text-gray-700">
                Display Label *
              </label>
              <input
                type="text"
                name="display_label"
                id="display_label"
                value={formData.display_label}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.display_label ? 'border-red-300' : ''
                }`}
                placeholder="e.g., Consulting Services, Software Development"
              />
              {validationErrors.display_label && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.display_label}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                User-friendly name displayed in the interface
              </p>
            </div>

            {/* Parent Category */}
            <div>
              <label htmlFor="parent_category_id" className="block text-sm font-medium text-gray-700">
                Parent Category
              </label>
              <select
                name="parent_category_id"
                id="parent_category_id"
                value={formData.parent_category_id}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.parent_category_id ? 'border-red-300' : ''
                }`}
              >
                <option value="">No Parent (Top Level)</option>
                {getAvailableParentCategories().map(category => (
                  <option key={category.id} value={category.id}>
                    {category.display_label}
                  </option>
                ))}
              </select>
              {validationErrors.parent_category_id && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.parent_category_id}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Optional: Select a parent category to create a hierarchy
              </p>
            </div>

            {/* Sort Order */}
            <div>
              <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700">
                Sort Order
              </label>
              <input
                type="number"
                name="sort_order"
                id="sort_order"
                min="1"
                value={formData.sort_order}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.sort_order ? 'border-red-300' : ''
                }`}
              />
              {validationErrors.sort_order && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.sort_order}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Controls the display order (lower numbers appear first)
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              name="description"
              id="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Optional description of this category..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional description to help users understand this category
            </p>
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              id="is_active"
              name="is_active"
              type="checkbox"
              checked={formData.is_active}
              onChange={handleChange}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
              Active
            </label>
            <p className="ml-2 text-xs text-gray-500">
              Inactive categories are hidden from selection lists
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/price-book/categories')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <TagIcon className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Category' : 'Create Category'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryForm; 